import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArtistPhoto } from "@/components/schedule/ArtistPhoto";
import { FilmedByList } from "@/components/schedule/FilmedByList";
import { useArtistImagePrefetch } from "@/hooks/useArtistImagePrefetch";
import { briefingDayDoorsQuery, briefingStagesQuery } from "@/lib/queries/productionQueries";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ZoomIn, ZoomOut, GripVertical, Star, Highlighter } from "lucide-react";
import { ScheduleFeedbackMarker } from "@/components/ScheduleFeedbackMarker";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { getStageColor } from "@/lib/stageColors";
import { matchesStage, orderedStagesForDay, stageKeyFor } from "@/lib/scheduleStages";
import { stagesForDay, type BriefingStageRecord } from "@/lib/briefingStages";
import { applyStageFilter, toggleStage } from "@/lib/stageFilterUi";
import { artistGlowVisible } from "@/lib/lineupHighlight";
import { isCustomForStage, isStageBoundCustom } from "@/lib/stageAssignments";
import { StageFilterBar } from "@/components/schedule/StageFilterBar";
import { FunctionFilterBar } from "@/components/schedule/FunctionFilterBar";
import { ScheduleRowResizeHandles } from "@/components/schedule/ScheduleRowResizeHandles";
import { useScheduleRowHeights } from "@/hooks/useScheduleRowHeights";
import { useCurrentTimeMarker } from "@/hooks/useCurrentTimeMarker";
import { useProducerTimeFormatter } from "@/hooks/useTimeFormatter";
import { READONLY_LAYERS } from "@/lib/scheduleLayers";
import { LABEL_SEAM_COVER, ROW_SEAM } from "@/lib/scheduleRowSeams";
import { CurrentTimeMarkerLine } from "./schedule/CurrentTimeMarkerLine";
import { CurrentTimeMarkerPill } from "./schedule/CurrentTimeMarkerPill";
import { TimelineHourLabels } from "./schedule/TimelineHourLabels";
import { useScheduleLabelWidth } from "@/hooks/useScheduleLabelWidth";
import { HOUR_WIDTH_PX } from "@/lib/timeSnap";
import {
  customRowKey,
  dutyBlockLayout,
  stackedBlockLayout,
  stageDutyStrip,
  stageDutyStripLanes,
  stageRowKey,
} from "@/lib/scheduleRowHeights";
import { assignRowLanes, maxLaneCount } from "@/lib/scheduleRowLanes";
import { sortStackedAssignments } from "@/lib/stackOrder";
import { useStageReorder } from "@/hooks/useStageReorder";
import { useReorderStages } from "@/hooks/useReorderStages";
import { parseStages } from "@/types/domain";
import type { Json } from "@/integrations/supabase/types";
import { useProducerSettings } from "@/hooks/useProducerSettings";
import {
  applyFunctionFilter,
  crewPassesFunctionFilter,
  toggleFunction,
} from "@/lib/crewFunctionFilter";
import {
  festivalDayIso,
  resolveScheduleStartHour,
  normalizeTimelineHour,
  horizontalPositionForTime,
  AFTER_MIDNIGHT_THRESHOLD,
  doorsForDay,
  type DayDoors,
} from "@produce/shared";

interface Artist {
  id?: string;
  artist_name: string;
  stage_name: string;
  set_start: string;
  set_end: string;
  event_date: string;
  // Producer-set "important artist" flag (lineup.highlighted). Persisted in the
  // schedule creator (LineupImportTab star toggle) and painted here as a green
  // glow so the read-only / share view surfaces the same emphasis. Distinct from
  // the search-based `highlightArtistName` highlight below (lime ring + star).
  highlighted?: boolean;
}

interface Assignment {
  id?: string;
  crew_member_id: string;
  event_date: string;
  artist_name?: string;
  stage_name: string;
  stage_id?: string | null;
  lineup_id?: string | null;
  filming_start: string;
  filming_end: string;
  assignment_type: "artist" | "custom";
  subject?: string;
  notes?: string;
  location?: string | null;
  // Set on server-generated mirror rows (dedicated-assistant copies).
  mirrored_from_id?: string | null;
}

interface CrewMember {
  id: string;
  name: string;
  function: string;
  color: string | null;
  assists_crew_member_id?: string | null;
}

interface ReadOnlyScheduleTimelineProps {
  briefing: { id: string; stages?: Json | null; doors_open?: string | null };
  lineup: Artist[];
  assignments: Assignment[];
  crewMembers: CrewMember[];
  eventDays: string[];
  selectedDay: string;
  onDayChange: (day: string) => void;
  // Share mode: the public client-review page hosts this component without an
  // authenticated session. It injects the data the component would otherwise
  // fetch itself (stages, per-day doors, time-format setting) and disables every
  // producer-only affordance (the "Edit schedule" shortcut, stage drag-reorder).
  // Omitting these keeps the producer's existing usage 100% unchanged.
  shareMode?: boolean;
  stagesOverride?: BriefingStageRecord[];
  dayDoorsOverride?: DayDoors[];
  timeFormatOverride?: "24h" | "12h";
  // Client feedback: when set, clicking a spot on a stage row (scheduled or
  // empty) calls onAddFeedback with the click anchor (stage + rounded time +
  // artist when the click landed on one). Used by the public review page.
  onAddFeedback?: (anchor: FeedbackAnchor) => void;
  // Producer view: red "!" markers rendered at each feedback's anchor; clicking
  // one opens a popover to read it and resolve/delete.
  feedbackMarkers?: FeedbackMarker[];
  onResolveFeedback?: (id: string) => void;
  onDeleteFeedback?: (id: string) => void;
  // Client view: lets the author edit their own feedback from the marker popover.
  onEditFeedback?: (marker: FeedbackMarker) => void;
  // When set, the matching marker is scrolled into view + highlighted (used when
  // the producer clicks a feedback item in the list to locate it on the grid).
  focusedFeedbackId?: string | null;
  // Artist search: scrolls the matching artist block into view and highlights
  // it (with a star), the way the lineup upload highlights a focused artist.
  highlightArtistName?: string | null;
}

export interface FeedbackAnchor {
  stage_name: string;
  anchor_time: string;
  artist_name?: string | null;
}

export interface FeedbackMarker {
  id: string;
  stage_name: string | null;
  anchor_time: string | null;
  author_name: string;
  body: string;
  status?: string;
}

/**
 * Tailwind `p-4` on the scroll canvas (see the timeline container below). The
 * time pill is positioned against the scroll container itself, so it has to add
 * the padding the rows sit behind. Keep in sync with that class.
 */
const CANVAS_PADDING = 16;

export function ReadOnlyScheduleTimeline({
  briefing,
  lineup,
  assignments,
  crewMembers,
  eventDays,
  selectedDay,
  onDayChange,
  shareMode = false,
  stagesOverride,
  dayDoorsOverride,
  timeFormatOverride,
  onAddFeedback,
  feedbackMarkers,
  onResolveFeedback,
  onDeleteFeedback,
  onEditFeedback,
  focusedFeedbackId,
  highlightArtistName,
}: ReadOnlyScheduleTimelineProps) {
  const { settings: producerSettings } = useProducerSettings();
  const [zoomLevel, setZoomLevel] = useState(1);
  // Show/hide the producer "important artist" glows, mirroring the schedule
  // creator's Highlighter toolbar toggle. Defaults to on so marked artists
  // stand out immediately.
  const [showHighlights, setShowHighlights] = useState(true);
  const [crewVisibility, setCrewVisibility] = useState<Record<string, boolean>>({});
  // Multi-select filter on crew function (e.g. "One-take shooter",
  // "Aftermovie shooter"). Empty = no filter, every crew is in scope.
  // Compose with crewVisibility: a crew is rendered iff its function
  // is in scope AND its visibility toggle is on.
  const [selectedFunctions, setSelectedFunctions] = useState<Set<string>>(new Set());
  // Multi-select stage filter (empty = show all stages).
  const [selectedStages, setSelectedStages] = useState<Set<string>>(new Set());
  // Artist detail popup (producer view): photo + who films this artist + times.
  const [detailArtist, setDetailArtist] = useState<{
    artist: Artist;
    assignments: Assignment[];
  } | null>(null);

  // Warm every artist photo when the schedule opens so the popup shows the
  // image instantly instead of fetching on first click. Skipped for the public
  // client-review page (no producer session to call the edge function with).
  useArtistImagePrefetch(
    useMemo(() => (shareMode ? [] : lineup.map((a) => a.artist_name)), [shareMode, lineup]),
  );
  const timelineRef = useRef<HTMLDivElement>(null);
  const resolveScroller = useCallback(() => timelineRef.current, []);
  const navigate = useNavigate();
  const hasAutoScrolled = useRef(false);
  const normalizedHighlight = (highlightArtistName ?? "").trim().toLowerCase();

  // Honours the producer's 12h/24h setting, or the one injected with the
  // share payload when there is no authenticated producer.
  const formatTime = useProducerTimeFormatter(timeFormatOverride);

  // Initialize crew visibility (all visible by default)
  useEffect(() => {
    const initialVisibility: Record<string, boolean> = {};
    crewMembers.forEach((crew) => {
      initialVisibility[crew.id] = true;
    });
    setCrewVisibility(initialVisibility);
  }, [crewMembers]);

  // Get lineup for selected day
  const lineupForDay = lineup.filter((a) => a.event_date === selectedDay);
  // Assignments for the selected day — used to keep stages that have crew
  // planned on them (e.g. overview / audience) visible even with no lineup.
  const assignmentsForDay = assignments.filter((a) => a.event_date === selectedDay);

  // Canonical stage order from briefing_stages.display_order — the same source
  // the schedule creator uses, so drag-reordering here and there stays in sync.
  const { data: fetchedBriefingStages = [] } = useQuery({
    ...briefingStagesQuery(briefing.id),
    enabled: !shareMode && !!briefing.id,
    select: (rows) => rows as BriefingStageRecord[],
  });
  const briefingStages = shareMode ? (stagesOverride ?? []) : fetchedBriefingStages;
  const dayStageRecords = stagesForDay(briefingStages, selectedDay);
  // Stage ids for the selected day. A custom assignment bound to one of
  // these renders inside that stage's row instead of the per-crew Custom row.
  const dayStageIdSet = new Set(dayStageRecords.map((s) => s.id));

  // Per-day doors-open overrides, so the timeline starts at the festival's
  // opening time for the selected day (falling back to the briefing-level
  // doors_open). Resolved with the same shared helper the form uses.
  const { data: fetchedDayDoors = [] } = useQuery({
    ...briefingDayDoorsQuery(briefing.id),
    enabled: !shareMode && !!briefing.id,
    select: (rows) => rows as DayDoors[],
  });
  const dayDoors = shareMode ? (dayDoorsOverride ?? []) : fetchedDayDoors;
  const doorsOpenForDay = doorsForDay({
    perDay: dayDoors,
    fallback: { doors_open: briefing.doors_open ?? null, doors_close: null },
    eventDate: selectedDay,
  }).doors_open;

  // Prefer the canonical (reorderable) order; fall back to the legacy
  // briefings.stages JSONB for old briefings with no briefing_stages rows.
  const allStages =
    dayStageRecords.length > 0
      ? dayStageRecords
          .map((rec) => ({ name: rec.label, label: rec.label, id: rec.id }))
          .filter(
            (s) =>
              lineupForDay.some((row) => matchesStage(row, s)) ||
              // Keep a lineup-less stage if crew is planned on it (overview /
              // audience filming), so it still shows up and can be filtered.
              assignmentsForDay.some((a) => isCustomForStage(a, s.id)),
          )
      : orderedStagesForDay(parseStages(briefing.stages ?? null), lineupForDay);

  // Apply the (optional) stage filter. Empty selection = show every stage.
  // Shared with the schedule creator via stageFilterUi.
  const stages = applyStageFilter(allStages, selectedStages);

  const reorderStagesMutation = useReorderStages(briefing.id, selectedDay);

  const stageReorder = useStageReorder(
    dayStageRecords.map((s) => s.id),
    (orderedIds) => reorderStagesMutation.mutate(orderedIds),
  );

  // Resolve the assisted member's name for a mirror row, so view-only blocks
  // can show "follows X". Mirror rows reference their source assignment, whose
  // crew_member_id is the assisted member.
  const assignmentById = useMemo(
    () => new Map(assignments.filter((a) => a.id).map((a) => [a.id as string, a])),
    [assignments],
  );
  const mirrorSourceName = (a: Assignment): string | null => {
    if (!a.mirrored_from_id) return null;
    const src = assignmentById.get(a.mirrored_from_id);
    const member = src ? crewMembers.find((c) => c.id === src.crew_member_id) : null;
    return member?.name ?? null;
  };

  // Calculate actual time range from lineup and assignments
  const allTimes = [
    ...lineupForDay.map((a) => ({ start: a.set_start, end: a.set_end })),
    ...assignments
      .filter((a) => a.event_date === selectedDay)
      .map((a) => ({ start: a.filming_start, end: a.filming_end })),
  ];

  // Start the timeline at the festival's doors-open time, pulled back if any
  // item starts earlier so nothing falls off the left edge. Shared with the
  // schedule creator via resolveScheduleStartHour (threshold = 06:00).
  const startHour = resolveScheduleStartHour({
    doorsOpen: doorsOpenForDay,
    itemStartTimes: allTimes.map((t) => t.start),
    fallback: 9,
  });

  let maxHour = startHour + 24;
  if (allTimes.length > 0) {
    const endTimes = allTimes
      .map((t) => normalizeTimelineHour(t.end, AFTER_MIDNIGHT_THRESHOLD))
      .filter((h): h is number => h !== null);
    if (endTimes.length > 0) {
      maxHour = Math.max(startHour + 1, Math.ceil(Math.max(...endTimes)) + 1); // +1h padding
    }
  }
  const endHour = maxHour;
  const hourWidth = HOUR_WIDTH_PX; // shared with the schedule creator
  const rowMinHeight = 80;
  // The artist container carries Tailwind `top-2`; assignment blocks inside it
  // position relative to that offset (see rowBlockBand).
  const ARTIST_CONTAINER_TOP = 8;
  // Per-row adjustable heights (persisted per briefing in localStorage, so
  // they also work for the unauthenticated client-share page).
  const rowHeights = useScheduleRowHeights(briefing.id);
  // Adjustable label-column width (same persistence mechanism; shared with
  // the schedule creator, each view keeping its own default).
  const labelWidthCtl = useScheduleLabelWidth(briefing.id, "readonly", 180);
  const { labelWidth } = labelWidthCtl;

  // Convert time to position (shared with the schedule creator's timeline).
  const timeToPosition = (time: string) =>
    horizontalPositionForTime(time, startHour, hourWidth * zoomLevel);

  // Calculate duration
  const getDuration = (start: string, end: string) => {
    return timeToPosition(end) - timeToPosition(start);
  };

  // Inverse of timeToPosition: turn a pixel offset within a stage grid into a
  // clock time rounded to the nearest 15 minutes. Used to anchor client feedback
  // to wherever they clicked (scheduled or empty). Hours past 24 wrap to the
  // next day's clock (e.g. 25:00 -> 01:00), matching how blocks are positioned.
  const positionToTime = (px: number): string => {
    const totalMinutes = (px / (hourWidth * zoomLevel)) * 60;
    const rounded = Math.round(totalMinutes / 15) * 15;
    let absMinutes = startHour * 60 + rounded;
    absMinutes = Math.max(startHour * 60, Math.min(endHour * 60, absMinutes));
    const clockHour = Math.floor(absMinutes / 60) % 24;
    const minute = absMinutes % 60;
    return `${String(clockHour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  };

  // Markers for the currently selected day, grouped by stage label so each row
  // can render its own. Only feedback with a stage + time anchor shows on the
  // grid; general feedback stays in the list below.
  const markersByStage = useMemo(() => {
    const map = new Map<string, FeedbackMarker[]>();
    for (const m of feedbackMarkers ?? []) {
      if (!m.stage_name || !m.anchor_time) continue;
      const list = map.get(m.stage_name) ?? [];
      list.push(m);
      map.set(m.stage_name, list);
    }
    return map;
  }, [feedbackMarkers]);

  // Client click on a stage grid → resolve stage + time (+ artist if the click
  // landed on a scheduled set) and hand it to the page's feedback composer.
  const handleGridClick = (
    e: React.MouseEvent<HTMLDivElement>,
    stage: { label: string },
    stageArtists: Artist[],
  ) => {
    if (!onAddFeedback) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const anchorTime = positionToTime(px);
    const hit = stageArtists.find(
      (a) => px >= timeToPosition(a.set_start) && px < timeToPosition(a.set_end),
    );
    onAddFeedback({
      stage_name: stage.label,
      anchor_time: anchorTime,
      artist_name: hit?.artist_name ?? null,
    });
  };

  // Render the feedback dots for one row (stage label or "Custom - <name>").
  // Producers (handlers present) can resolve/delete and jump to the creator;
  // the client just reads the comment + its status.
  // 15-min (subtle) + hour (thick) vertical grid lines for one row's grid.
  const renderGridLines = () =>
    Array.from({ length: (endHour - startHour) * 4 }).map((_, i) => {
      const isHourLine = i % 4 === 0;
      return (
        <div
          key={i}
          className="absolute bottom-0 top-0 border-r"
          style={{
            left: i * (hourWidth / 4) * zoomLevel,
            borderColor: isHourLine ? "hsl(var(--border))" : "hsl(var(--border) / 0.15)",
            borderWidth: isHourLine ? "2px" : "1px",
          }}
        />
      );
    });

  const renderFeedbackMarkers = (label: string) =>
    (markersByStage.get(label) ?? []).map((m) => (
      <ScheduleFeedbackMarker
        formatTime={formatTime}
        key={m.id}
        marker={m}
        left={timeToPosition(m.anchor_time!)}
        focused={focusedFeedbackId === m.id}
        onResolve={onResolveFeedback}
        onDelete={onDeleteFeedback}
        onEdit={onEditFeedback ? () => onEditFeedback(m) : undefined}
        onOpenInCreator={
          !shareMode && (onResolveFeedback || onDeleteFeedback)
            ? () => navigate(`/briefings/${briefing.id}/edit?step=5&shortcut=true`)
            : undefined
        }
      />
    ));

  // Check if today is selected day
  const isToday = selectedDay === festivalDayIso();

  // Live "now" marker — position follows the current startHour/zoom on every
  // render, so it can't be left behind when the day's data re-anchors the grid.
  const currentTimeMarker = useCurrentTimeMarker({
    startHour,
    pixelsPerHour: hourWidth * zoomLevel,
    spanHours: endHour - startHour,
    enabled: isToday && (producerSettings?.schedule_show_current_time_marker ?? true),
  });

  // Auto-scroll to current time on initial load
  useEffect(() => {
    if (
      producerSettings?.schedule_auto_scroll_current_time &&
      isToday &&
      timelineRef.current &&
      !hasAutoScrolled.current
    ) {
      // Offset to show some context before current time
      const scrollPosition = currentTimeMarker.position - 200;
      if (scrollPosition > 0) {
        timelineRef.current.scrollLeft = scrollPosition;
        hasAutoScrolled.current = true;
      }
    }
  }, [producerSettings?.schedule_auto_scroll_current_time, isToday, currentTimeMarker.position]);

  // Artist search: scroll the highlighted artist block into view (centered).
  useEffect(() => {
    if (!normalizedHighlight || !timelineRef.current) return;
    const el = timelineRef.current.querySelector<HTMLElement>('[data-artist-highlight="true"]');
    if (el) el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [normalizedHighlight, selectedDay, zoomLevel]);

  // Focused feedback (producer clicked a list item): scroll its marker into view.
  useEffect(() => {
    if (!focusedFeedbackId || !timelineRef.current) return;
    const el = timelineRef.current.querySelector<HTMLElement>(
      `[data-feedback-id="${focusedFeedbackId}"]`,
    );
    if (el) el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [focusedFeedbackId, selectedDay, zoomLevel]);

  // Zoom handlers
  const handleZoomIn = () => setZoomLevel((prev) => Math.min(prev + 0.25, 2));
  const handleZoomOut = () => setZoomLevel((prev) => Math.max(prev - 0.25, 0.5));
  const handleZoomReset = () => setZoomLevel(1);

  // Toggle crew visibility
  const toggleCrewVisibility = (crewId: string) => {
    setCrewVisibility((prev) => ({ ...prev, [crewId]: !prev[crewId] }));
  };

  // Get visible crew members
  // Function filter narrows the crew set BEFORE the per-crew toggles.
  // Both filters compose: a crew is rendered iff (no function filter
  // OR its function is selected) AND its individual visibility is on.
  const crewMembersInScope = applyFunctionFilter(crewMembers, selectedFunctions);
  const visibleCrew = crewMembersInScope.filter((crew) => crewVisibility[crew.id]);

  // Get custom subjects per crew member. Stage-bound customs are excluded
  // here — they render inside their stage's row instead.
  const getCustomSubjectsForCrew = (crewId: string) => {
    return assignments.filter(
      (a) =>
        a.crew_member_id === crewId &&
        a.assignment_type === "custom" &&
        !isStageBoundCustom(a, dayStageIdSet),
    );
  };

  // Get assignments for an artist. The two filters compose: function
  // filter must pass AND the per-crew visibility toggle must be on.
  const isCrewVisible = (crewId: string): boolean => {
    if (!crewVisibility[crewId]) return false;
    if (selectedFunctions.size === 0) return true;
    const member = crewMembers.find((c) => c.id === crewId);
    if (!member) return false;
    return crewPassesFunctionFilter(member.function, selectedFunctions);
  };

  const getArtistAssignments = (artist: Artist) => {
    // Mirrors last (assistant sits below the filming member), then a stable
    // alphabetical order — see sortStackedAssignments.
    return sortStackedAssignments(
      assignments.filter((a) => {
        if (a.assignment_type !== "artist" || !isCrewVisible(a.crew_member_id)) return false;
        // FK match: crew_assignments.lineup_id ↔ lineup.id
        // (post-migration 20260504240200). The trigger
        // set_crew_assignment_lineup_id auto-fills this on every
        // artist-type INSERT/UPDATE; a NULL here means the filming
        // window didn't fit any slot — show nothing rather than
        // silently rebind by text triple, which would mask data bugs.
        return !!(a.lineup_id && artist.id && a.lineup_id === artist.id);
      }),
      (a) => !!a.mirrored_from_id,
      (crewId) => crewMembers.find((c) => c.id === crewId)?.name ?? "",
    );
  };

  const timelineWidth = labelWidth + (endHour - startHour) * hourWidth * zoomLevel;

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-6">
        {/* Controls Bar */}
        <div className="flex items-center gap-4">
          {/* Zoom controls - far left */}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={handleZoomOut}>
              <ZoomOut className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={handleZoomReset}>
              {Math.round(zoomLevel * 100)}%
            </Button>
            <Button variant="outline" size="icon" onClick={handleZoomIn}>
              <ZoomIn className="h-4 w-4" />
            </Button>
            {/* Highlight toggle: show/hide the producer's "important artist"
                glows, mirroring the schedule creator's Highlighter button.
                Producer-only control — hidden for the external share view,
                where highlights simply stay on. */}
            {!shareMode && (
              <Button
                variant="outline"
                size="icon"
                onClick={() => setShowHighlights((v) => !v)}
                title={showHighlights ? "Hide artist highlights" : "Show artist highlights"}
                aria-pressed={showHighlights}
              >
                <Highlighter
                  className={`h-4 w-4 ${showHighlights ? "text-primary" : "text-muted-foreground"}`}
                />
              </Button>
            )}
          </div>

          {/* Edit schedule button - next to zoom. Hidden for the external
              client (share mode): they have no producer session to edit with. */}
          {!shareMode && (
            <Button
              variant="outline"
              onClick={() => navigate(`/briefings/${briefing.id}/edit?step=5&shortcut=true`)}
            >
              Edit schedule
            </Button>
          )}

          <div className="flex-1" />

          {/* Day selector - right side */}
          {eventDays.length > 1 && (
            <Select value={selectedDay} onValueChange={onDayChange}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {eventDays.map((day) => (
                  <SelectItem key={day} value={day}>
                    {format(new Date(day), "EEEE, MMM d")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Main Timeline - scrollable container */}
        <div
          ref={timelineRef}
          className="relative overflow-x-auto rounded-lg border border-border bg-background"
        >
          <div style={{ width: `${timelineWidth}px` }} className="p-4">
            {/* Time Header */}
            <div className="mb-2 flex bg-background">
              {/* Left label spacer */}
              <div
                className="sticky left-0 z-40 border-r border-border bg-background"
                style={{ width: labelWidth }}
              />

              {/* Hour labels */}
              <div
                className="relative"
                style={{ width: (endHour - startHour) * hourWidth * zoomLevel }}
              >
                <TimelineHourLabels
                  startHour={startHour}
                  hours={endHour - startHour}
                  pixelsPerHour={hourWidth * zoomLevel}
                  formatTime={formatTime}
                  resolveScroller={resolveScroller}
                  canvasPadding={CANVAS_PADDING}
                  className="absolute -translate-x-1/2 text-sm font-medium text-muted-foreground"
                  top={-20}
                />
              </div>
            </div>

            {/* Stage Rows */}
            {stages.map((stage) => {
              const stageRowId = (stage as { id?: string }).id;
              const stageArtists = lineupForDay.filter((a) => matchesStage(a, stage));
              const stageCustomAssignments = stageRowId
                ? assignmentsForDay.filter(
                    (a) => isCustomForStage(a, stageRowId) && isCrewVisible(a.crew_member_id),
                  )
                : [];
              // Two bands per row: artist sets + their crew on top, a
              // separate stage-duties strip at the bottom for stage-bound
              // customs ("overzicht stage", "publiek", …) so those can't be
              // mistaken for artist assignments. Lanes are computed per band;
              // position (px) is a monotonic stand-in for time and already
              // handles after-midnight normalization.
              const artistAssignmentsByArtist = new Map(
                stageArtists.map((artist) => [artist, getArtistAssignments(artist)]),
              );
              const laneRange = (a: Assignment) => ({
                start: timeToPosition(a.filming_start),
                end: timeToPosition(a.filming_end),
              });
              const artistLanes = assignRowLanes(
                stageArtists.flatMap((artist) => artistAssignmentsByArtist.get(artist) ?? []),
                laneRange,
              );
              const stageLanes = assignRowLanes(stageCustomAssignments, laneRange);
              const stageColor = getStageColor(stageKeyFor(stage));
              // Reorder is a producer-only affordance: disabled in share mode so
              // the external client can't drag stages (and has no session to
              // persist a reorder with anyway).
              const stageId = shareMode ? undefined : stageRowId;
              const rowKey = stageRowKey(stage);
              const rowHeight = rowHeights.getRowHeight(rowKey, rowMinHeight);
              // Inner area between the row's vertical padding (the artist
              // container's top-2 + matching bottom gap). Artists keep this
              // full area; the stage-duties strip is APPENDED below it, so
              // the row grows instead of the artist blocks shrinking. On
              // stages without artists the duties fill the area itself.
              const rowArea = {
                top: ARTIST_CONTAINER_TOP,
                height: rowHeight - 2 * ARTIST_CONTAINER_TOP,
              };
              // Same rule as the editor, minus the reserved drop lane: this
              // view takes no drops, so an empty stage row keeps its old
              // height here (the creator's rows are one lane taller).
              const stageStrip = stageDutyStrip(
                rowArea,
                stageDutyStripLanes({
                  hasStageRow: true,
                  hasArtists: stageArtists.length > 0,
                  dutyLanes: maxLaneCount(stageLanes),
                }),
              );
              const totalRowHeight = rowHeight + stageStrip.extraHeight;

              return (
                <div key={stageKeyFor(stage)} className="relative flex border-b border-border">
                  {/* Left label with stage color - STICKY (higher z-index than artist text).
                      Drag the label to reorder stages (persists to briefing_stages). */}
                  <div
                    {...(stageId ? stageReorder.getStageProps(stageId) : {})}
                    className={`sticky left-0 ${READONLY_LAYERS.stickyLabel.className} ${LABEL_SEAM_COVER} ${ROW_SEAM.readOnly.coverClass} flex items-center gap-1 border-r border-border bg-background p-3 after:content-[''] ${
                      stageId ? "cursor-grab active:cursor-grabbing" : ""
                    } ${stageId && stageReorder.overId === stageId ? "ring-2 ring-inset ring-primary" : ""} ${
                      stageId && stageReorder.draggingId === stageId ? "opacity-50" : ""
                    }`}
                    style={{
                      width: labelWidth,
                      height: totalRowHeight,
                      borderLeft: `4px solid ${stageColor}`,
                    }}
                    title={stageId ? "Drag to reorder stages" : undefined}
                  >
                    {stageId && <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground" />}
                    <div className="min-w-0 truncate font-medium">{stage.label}</div>
                    <ScheduleRowResizeHandles
                      rowKey={rowKey}
                      rowHeight={rowHeight}
                      rowHeights={rowHeights}
                      labelWidth={labelWidthCtl}
                    />
                  </div>

                  {/* Timeline grid. In client share mode the grid is clickable
                      to drop a feedback point anywhere (scheduled or empty). */}
                  <div
                    className={`relative flex-1 bg-background ${onAddFeedback ? "cursor-crosshair" : ""}`}
                    style={{ height: totalRowHeight }}
                    onClick={
                      onAddFeedback ? (e) => handleGridClick(e, stage, stageArtists) : undefined
                    }
                  >
                    {/* Grid lines - every 15 minutes (subtle) and hour (thick) */}
                    {renderGridLines()}

                    {/* Current time marker: one segment per row, so it can't
                        bleed above or below the grid onto the labels. */}
                    {currentTimeMarker.visible && (
                      <CurrentTimeMarkerLine
                        left={currentTimeMarker.position}
                        zClass={READONLY_LAYERS.currentTimeLine.className}
                        bridge={ROW_SEAM.readOnly.bridgePx}
                      />
                    )}

                    {/* Feedback markers for this stage (red "!" = open, green
                        check = resolved). */}
                    {renderFeedbackMarkers(stage.label)}

                    {/* Artist blocks */}
                    {stageArtists.map((artist, idx) => {
                      const artistAssignments = artistAssignmentsByArtist.get(artist) ?? [];
                      const left = timeToPosition(artist.set_start);
                      const width = getDuration(artist.set_start, artist.set_end);
                      const isHighlighted =
                        !!normalizedHighlight &&
                        (artist.artist_name ?? "").toLowerCase().includes(normalizedHighlight);
                      // Producer-marked important artist (persisted lineup.highlighted),
                      // gated by the Highlighter toolbar toggle (same as the creator).
                      const showGlow = artistGlowVisible(artist.highlighted, showHighlights);

                      return (
                        <Tooltip key={idx}>
                          <TooltipTrigger asChild>
                            <div
                              data-artist-highlight={isHighlighted ? "true" : undefined}
                              onClick={
                                shareMode
                                  ? undefined
                                  : (e) => {
                                      e.stopPropagation();
                                      // Full filmer list for the popup — NOT the
                                      // visibility/function-filtered timeline map,
                                      // so "Filmed by" always lists everyone who
                                      // films this artist.
                                      setDetailArtist({
                                        artist,
                                        assignments: assignments.filter(
                                          (a) =>
                                            a.assignment_type === "artist" &&
                                            !!(
                                              a.lineup_id &&
                                              artist.id &&
                                              a.lineup_id === artist.id
                                            ),
                                        ),
                                      });
                                    }
                              }
                              className={`absolute top-2 cursor-pointer overflow-visible rounded-md ${
                                isHighlighted
                                  ? "z-30 animate-pulse ring-4 ring-[#d1ff38] ring-offset-2 ring-offset-background"
                                  : ""
                              }`}
                              style={{
                                left: `${left}px`,
                                width: `${width}px`,
                                height: `${rowArea.height}px`,
                              }}
                            >
                              {isHighlighted && (
                                <span className="absolute -right-2 -top-2 z-40 flex h-5 w-5 items-center justify-center rounded-full bg-[#d1ff38] text-[#091527] shadow-md">
                                  <Star className="h-3 w-3 fill-current" />
                                </span>
                              )}
                              {/* Layer 1: Artist background bar (glass effect) */}
                              <div
                                className="absolute inset-0 z-10 rounded-md border border-white/30 transition-opacity hover:opacity-80"
                                style={{
                                  backgroundColor: "rgba(255, 255, 255, 0.08)",
                                  backdropFilter: "blur(4px)",
                                }}
                              />

                              {/* Layer 2: Crew assignment blocks (solid) with layering for overlaps */}
                              {artistAssignments.map((assignment, aIdx) => {
                                const crew = crewMembers.find(
                                  (c) => c.id === assignment.crew_member_id,
                                );
                                if (!crew) return null;

                                const assignLeft = timeToPosition(assignment.filming_start);
                                const assignWidth = getDuration(
                                  assignment.filming_start,
                                  assignment.filming_end,
                                );

                                // Lane within the artist band; positions are
                                // relative to the artist container, blocks sit
                                // 4px inset within it.
                                const lane = artistLanes.get(assignment) ?? {
                                  laneIndex: 0,
                                  laneCount: 1,
                                };
                                const { top: topOffset, height: blockHeight } = stackedBlockLayout({
                                  innerTop: 4,
                                  innerHeight: Math.max(0, rowArea.height - 8),
                                  totalOverlapping: lane.laneCount,
                                  overlapIndex: lane.laneIndex,
                                });

                                const isMirror = !!assignment.mirrored_from_id;
                                // z-30: above the artist text layer so the
                                // block's own label (artist / film time / crew)
                                // isn't overprinted — matching the creator,
                                // where blocks also cover the set text. When
                                // blocks share the slot (stacked lanes) only
                                // the crew name is shown, like the creator.
                                return (
                                  <div
                                    key={aIdx}
                                    className={`absolute ${READONLY_LAYERS.block.className} overflow-hidden rounded border-2 ${
                                      isMirror ? "border-dashed border-white/80" : "border-white/20"
                                    }`}
                                    style={{
                                      left: `${assignLeft - left}px`,
                                      width: `${assignWidth}px`,
                                      top: `${topOffset}px`,
                                      height: `${blockHeight}px`,
                                      backgroundColor: crew.color || "hsl(var(--primary))",
                                    }}
                                  >
                                    <div className="flex h-full flex-col justify-end overflow-hidden p-1.5 text-xs font-medium text-white">
                                      {lane.laneCount <= 1 && (
                                        <div className="truncate text-[10px] uppercase tracking-wider opacity-80">
                                          {artist.artist_name}
                                        </div>
                                      )}
                                      {lane.laneCount <= 1 && (
                                        <div className="truncate opacity-70">
                                          {formatTime(assignment.filming_start)} -{" "}
                                          {formatTime(assignment.filming_end)}
                                        </div>
                                      )}
                                      <div className="truncate opacity-90 drop-shadow-md">
                                        {crew.name}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}

                              {/* Layer 3: Artist text (under the labeled crew
                                  blocks, above the glass background) */}
                              <div className="pointer-events-none absolute inset-0 z-20 flex flex-col justify-center p-2">
                                <div className="truncate text-sm font-semibold text-white drop-shadow-md">
                                  {artist.artist_name}
                                </div>
                                <div className="text-xs text-white drop-shadow-md">
                                  {formatTime(artist.set_start)} - {formatTime(artist.set_end)}
                                </div>
                              </div>

                              {/* Producer "important artist" glow. Painted as a
                                  top-layer, pointer-events-none overlay (like the
                                  schedule creator) so it frames the block above the
                                  crew-assignment blocks instead of being covered by
                                  them. Reuses the shared `glow-highlight` class +
                                  border-primary, identical to CrewSchedule. */}
                              {showGlow && (
                                <div
                                  aria-hidden
                                  className={`glow-highlight pointer-events-none absolute inset-0 ${READONLY_LAYERS.glow.className} rounded-md border-2 border-primary`}
                                />
                              )}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <div className="space-y-1">
                              <p className="font-semibold">{artist.artist_name}</p>
                              <p className="text-sm text-muted-foreground">{stage.label}</p>
                              <p className="text-sm">
                                Set: {formatTime(artist.set_start)} - {formatTime(artist.set_end)}
                              </p>
                              {artistAssignments.length > 0 && (
                                <div className="mt-2 space-y-1 border-t border-border pt-2">
                                  <p className="text-xs font-semibold">Assigned crew:</p>
                                  {artistAssignments.map((assignment, aIdx) => {
                                    const crew = crewMembers.find(
                                      (c) => c.id === assignment.crew_member_id,
                                    );
                                    if (!crew) return null;
                                    const filmingDiffersFromSet =
                                      assignment.filming_start.substring(0, 5) !==
                                        artist.set_start.substring(0, 5) ||
                                      assignment.filming_end.substring(0, 5) !==
                                        artist.set_end.substring(0, 5);
                                    const sourceName = mirrorSourceName(assignment);
                                    return (
                                      <div key={aIdx} className="text-xs">
                                        <p>
                                          {crew.name} – {crew.function}
                                          {sourceName && (
                                            <span className="text-muted-foreground">
                                              {" "}
                                              (follows {sourceName})
                                            </span>
                                          )}
                                        </p>
                                        {filmingDiffersFromSet && (
                                          <p className="text-muted-foreground">
                                            Film: {formatTime(assignment.filming_start)} -{" "}
                                            {formatTime(assignment.filming_end)}
                                          </p>
                                        )}
                                        {assignment.location && (
                                          <p className="text-muted-foreground">
                                            {assignment.location}
                                          </p>
                                        )}
                                        {assignment.notes && (
                                          <p className="text-muted-foreground">
                                            {assignment.notes}
                                          </p>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}

                    {/* Stage-level custom assignments (crew filming this
                        stage with no DJ — e.g. overview / audience). Shown in
                        the stage row, mirroring the schedule creator. */}
                    {stageCustomAssignments.map((assignment, idx) => {
                      const crew = crewMembers.find((c) => c.id === assignment.crew_member_id);
                      if (!crew) return null;
                      const left = timeToPosition(assignment.filming_start);
                      const width = getDuration(assignment.filming_start, assignment.filming_end);
                      // Fixed-height lane in the appended strip; on stages
                      // without artists the duties fill the row area itself.
                      const lane = stageLanes.get(assignment) ?? { laneIndex: 0, laneCount: 1 };
                      const { top: topOffset, height: blockHeight } = dutyBlockLayout(
                        stageStrip,
                        rowArea,
                        lane,
                      );
                      const isMirror = !!assignment.mirrored_from_id;
                      const sourceName = mirrorSourceName(assignment);
                      const dutyColor = crew.color || "hsl(var(--primary))";

                      return (
                        <Tooltip key={`stage-custom-${idx}`}>
                          <TooltipTrigger asChild>
                            <div
                              className={`absolute z-20 overflow-hidden rounded border-2 ${
                                isMirror ? "border-dashed border-white/80" : "border-white/20"
                              }`}
                              style={{
                                left: `${left}px`,
                                width: `${width}px`,
                                top: `${topOffset}px`,
                                height: `${blockHeight}px`,
                                backgroundColor: dutyColor,
                              }}
                            >
                              <div className="flex h-full items-center px-1.5 text-[10px] font-medium text-white">
                                <span className="truncate drop-shadow-md">
                                  {assignment.subject && (
                                    <span className="uppercase tracking-wider opacity-90">
                                      {assignment.subject} ·{" "}
                                    </span>
                                  )}
                                  {crew.name}
                                </span>
                              </div>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <div className="space-y-1">
                              <p className="font-semibold">{assignment.subject || "Custom"}</p>
                              <p className="text-sm text-muted-foreground">{stage.label}</p>
                              {assignment.location && (
                                <p className="text-sm text-muted-foreground">
                                  {assignment.location}
                                </p>
                              )}
                              <p className="text-sm">
                                {crew.name} – {crew.function}
                                {sourceName && (
                                  <span className="text-muted-foreground">
                                    {" "}
                                    (follows {sourceName})
                                  </span>
                                )}
                              </p>
                              <p className="text-sm">
                                Film: {formatTime(assignment.filming_start)} -{" "}
                                {formatTime(assignment.filming_end)}
                              </p>
                              {assignment.notes && (
                                <p className="text-sm text-muted-foreground">{assignment.notes}</p>
                              )}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* Custom Subject Rows per Crew Member */}
            {visibleCrew.map((crew) => {
              const customSubjects = getCustomSubjectsForCrew(crew.id);
              if (customSubjects.length === 0) return null;

              const rowKey = customRowKey(crew.id);
              const rowHeight = rowHeights.getRowHeight(rowKey, rowMinHeight);

              return (
                <div key={crew.id} className="relative flex border-b border-border">
                  {/* Left label - STICKY */}
                  <div
                    className={`sticky left-0 ${READONLY_LAYERS.stickyLabel.className} ${LABEL_SEAM_COVER} ${ROW_SEAM.readOnly.coverClass} flex items-center border-r border-border bg-background p-3 after:content-['']`}
                    style={{
                      width: labelWidth,
                      height: rowHeight,
                      borderLeft: `4px solid ${crew.color || "hsl(var(--primary))"}`,
                    }}
                  >
                    <div className="min-w-0 truncate text-sm font-medium">Custom - {crew.name}</div>
                    <ScheduleRowResizeHandles
                      rowKey={rowKey}
                      rowHeight={rowHeight}
                      rowHeights={rowHeights}
                      labelWidth={labelWidthCtl}
                    />
                  </div>

                  {/* Timeline grid (clickable for client feedback too). */}
                  <div
                    className={`relative flex-1 bg-background ${onAddFeedback ? "cursor-crosshair" : ""}`}
                    style={{ height: rowHeight }}
                    onClick={
                      onAddFeedback
                        ? (e) => handleGridClick(e, { label: `Custom - ${crew.name}` }, [])
                        : undefined
                    }
                  >
                    {/* Grid lines */}
                    {renderGridLines()}

                    {/* Current time marker: one segment per row, so it can't
                        bleed above or below the grid onto the labels. */}
                    {currentTimeMarker.visible && (
                      <CurrentTimeMarkerLine
                        left={currentTimeMarker.position}
                        zClass={READONLY_LAYERS.currentTimeLine.className}
                        bridge={ROW_SEAM.readOnly.bridgePx}
                      />
                    )}

                    {/* Feedback markers anchored to this custom row. */}
                    {renderFeedbackMarkers(`Custom - ${crew.name}`)}

                    {/* Custom subject blocks */}
                    {customSubjects.map((subject, idx) => {
                      const left = timeToPosition(subject.filming_start);
                      const width = getDuration(subject.filming_start, subject.filming_end);

                      return (
                        <Tooltip key={idx}>
                          <TooltipTrigger asChild>
                            <div
                              className="absolute top-2 z-20 flex cursor-pointer flex-col justify-center overflow-hidden rounded-md border-2 border-white/20 p-2 transition-opacity hover:opacity-90"
                              style={{
                                left: `${left}px`,
                                width: `${width}px`,
                                height: "calc(100% - 16px)",
                                backgroundColor: crew.color || "hsl(var(--primary))",
                                color: "white",
                                opacity: 1,
                              }}
                            >
                              <div className="truncate text-sm font-medium">{subject.subject}</div>
                              <div className="truncate text-xs opacity-90">
                                {formatTime(subject.filming_start)} -{" "}
                                {formatTime(subject.filming_end)}
                              </div>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <div className="space-y-1">
                              <p className="font-semibold">
                                {subject.subject || "Custom Assignment"}
                              </p>
                              {subject.stage_name &&
                                !subject.stage_name.startsWith("Custom") &&
                                subject.stage_name.toLowerCase() !==
                                  (subject.subject ?? "").toLowerCase() && (
                                  <p className="text-sm">{subject.stage_name}</p>
                                )}
                              {subject.location &&
                                subject.location.toLowerCase() !==
                                  (subject.subject ?? "").toLowerCase() &&
                                subject.location.toLowerCase() !==
                                  (subject.stage_name ?? "").toLowerCase() && (
                                  <p className="text-sm text-muted-foreground">
                                    {subject.location}
                                  </p>
                                )}
                              <p className="text-sm text-muted-foreground">
                                {crew.name} – {crew.function}
                              </p>
                              <p className="text-sm">
                                {formatTime(subject.filming_start)} -{" "}
                                {formatTime(subject.filming_end)}
                              </p>
                              {subject.notes && (
                                <p className="mt-2 border-t border-border pt-2 text-xs">
                                  {subject.notes}
                                </p>
                              )}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* Current time marker label - above header */}
            {currentTimeMarker.visible && (
              <CurrentTimeMarkerPill
                offset={currentTimeMarker.position}
                gridLeft={CANVAS_PADDING + labelWidth}
                label={formatTime(currentTimeMarker.time)}
                zClass={READONLY_LAYERS.currentTimeLabel.className}
                resolveScroller={resolveScroller}
                canvasPadding={CANVAS_PADDING}
              />
            )}
          </div>
        </div>

        {/* Crew Visibility Panel - Below timeline */}
        <Card className="w-full bg-background p-4 lg:w-fit">
          <StageFilterBar
            stages={allStages}
            selected={selectedStages}
            onToggle={(label) => setSelectedStages((prev) => toggleStage(prev, label))}
            onClear={() => setSelectedStages(new Set())}
          />
          <FunctionFilterBar
            crew={crewMembers}
            selected={selectedFunctions}
            onToggle={(fn) => setSelectedFunctions((prev) => toggleFunction(prev, fn))}
            onClear={() => setSelectedFunctions(new Set())}
            title="Filter by function"
          />
          <h3 className="mb-4 font-semibold">Crew visibility</h3>
          <div className="space-y-2">
            {crewMembersInScope.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No crew matches the selected function filter.
              </p>
            ) : (
              crewMembersInScope.map((crew) => (
                <div key={crew.id} className="flex items-center gap-3">
                  <Switch
                    checked={crewVisibility[crew.id] ?? true}
                    onCheckedChange={() => toggleCrewVisibility(crew.id)}
                  />
                  <div
                    className={`h-3 w-3 flex-shrink-0 rounded-full ${
                      crew.assists_crew_member_id
                        ? "outline-dashed outline-2 outline-offset-2 outline-muted-foreground"
                        : ""
                    }`}
                    style={{ backgroundColor: crew.color || "hsl(var(--primary))" }}
                    title={crew.assists_crew_member_id ? "Assistant" : undefined}
                  />
                  <Label className="cursor-pointer text-sm">
                    {crew.name}
                    <span className="block text-xs text-muted-foreground">{crew.function}</span>
                  </Label>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Artist detail popup: photo (enlargeable) + who films this artist. */}
        <Dialog open={!!detailArtist} onOpenChange={(open) => !open && setDetailArtist(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{detailArtist?.artist.artist_name}</DialogTitle>
            </DialogHeader>
            {detailArtist && (
              <div className="space-y-4">
                <ArtistPhoto artistName={detailArtist.artist.artist_name} />
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-muted-foreground">Stage</Label>
                    <p>{detailArtist.artist.stage_name}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Set Time</Label>
                    <p>
                      {formatTime(detailArtist.artist.set_start)} -{" "}
                      {formatTime(detailArtist.artist.set_end)}
                    </p>
                  </div>
                </div>
                <FilmedByList
                  items={detailArtist.assignments.flatMap((a, i) => {
                    const crew = crewMembers.find((c) => c.id === a.crew_member_id);
                    if (!crew) return [];
                    return [
                      {
                        key: a.id ?? String(i),
                        name: crew.name,
                        fn: crew.function,
                        sourceName: mirrorSourceName(a),
                        filming_start: a.filming_start,
                        filming_end: a.filming_end,
                      },
                    ];
                  })}
                  formatTime={formatTime}
                />
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
